import { SignedIn, SignedOut, SignInButton, useAuth } from "@clerk/clerk-react";
import type { OrderStatus } from "@the-drop/types";
import { wsOrdersNamespace } from "@the-drop/types";
import { useDriverLocationSocket } from "@the-drop/live-driver-map";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { resolveClerkPublishableKey } from "../clerk-env";
import { EtaCardBody, type EtaCardWindow } from "./eta-card-body";
import { fetchCustomerOrder, type CustomerOrderDetail } from "../lib/fetch-customer-order";
import { formatCentsAsUsd } from "../lib/format-menu";
import { getCustomerApiBaseUrl } from "../lib/api-base";
import { openSalesReceiptPdf } from "../lib/open-sales-receipt-pdf";

type Props = { dispensarySlug: string; orderId: string };

const TRACK_LABELS = ["Order placed", "Dispensary confirmed", "Driver assigned", "Out for delivery", "Delivered"] as const;

const COPY_LABEL_RESET_MS = 2_000;

/**
 * Per-status H1 + lede copy. `pending` reads as "Order received" —
 * dispensary acknowledgement happens at `confirmed`, so calling it
 * "Order confirmed" earlier is cosmetic dishonesty (redline 06 Q1
 * tightening / flag 2).
 */
function trackingHeading(status: OrderStatus): { h1: string; lede: string } {
  switch (status) {
    case "pending":
      return { h1: "Order received", lede: "We're letting the dispensary know about your order." };
    case "confirmed":
    case "dispatched":
      return { h1: "Order confirmed", lede: "Track your order in real time." };
    case "in_progress":
      return { h1: "Out for delivery", lede: "Your driver is on the way." };
    case "completed":
      return { h1: "Delivered", lede: "Thanks for shopping with us." };
    case "cancelled":
      return { h1: "Order cancelled", lede: "This order was cancelled before delivery." };
    case "failed":
      return { h1: "Delivery couldn't be completed", lede: "Reach out to support to resolve this order." };
  }
}

/**
 * Live timeline step, per status. Distinct from `orderProgress(status).index`
 * (which means "highest stage reached" and is used by the legacy bar's
 * fill-percentage). Here we want the step that's *currently in progress*:
 *   - pending → "Dispensary confirmed" pulses (placed step is Complete)
 *   - confirmed → "Driver assigned" pulses
 *   - dispatched + in_progress → both collapse to "Out for delivery" pulsing
 *     (distinguishing pre-pickup from post-pickup is internal-process detail)
 *   - completed → null (all five Complete)
 *   - cancelled / failed → null (banner shown instead of timeline)
 * See redline 06 flag 1.
 */
function timelineLiveIndex(status: OrderStatus): number | null {
  switch (status) {
    case "pending":
      return 1;
    case "confirmed":
      return 2;
    case "dispatched":
    case "in_progress":
      return 3;
    case "completed":
    case "cancelled":
    case "failed":
      return null;
  }
}

function timelineState(
  status: OrderStatus,
  stepIndex: number,
): "complete" | "live" | "pending" {
  if (status === "completed") return "complete";
  if (status === "cancelled" || status === "failed") return "pending";
  const live = timelineLiveIndex(status);
  if (live == null) return "pending";
  if (stepIndex < live) return "complete";
  if (stepIndex === live) return "live";
  return "pending";
}

/** UUIDs have dashes at fixed positions; raw `slice(-8)` lands on "-9E2B…".
 *  Strip dashes first so the short id is always 8 clean hex chars. */
function shortOrderId(orderId: string): string {
  return orderId.replace(/-/g, "").slice(-8).toUpperCase();
}

export function OrderTrackingScreen({ dispensarySlug, orderId }: Props) {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="mx-auto w-full max-w-[1200px] space-y-4 pt-6">
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-amber-900">
            Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to view order status.
          </p>
          <Link
            to="/$dispensarySlug/menu"
            params={{ dispensarySlug }}
            className="mt-3 inline-block text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--green)]"
          >
            ← Back to menu
          </Link>
        </div>
      </div>
    );
  }
  return <OrderTrackingInner dispensarySlug={dispensarySlug} orderId={orderId} />;
}

function OrderTrackingInner({ dispensarySlug, orderId }: Props) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [detail, setDetail] = useState<CustomerOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [receiptState, setReceiptState] = useState<{ kind: "idle" | "loading" } | { kind: "error"; message: string }>(
    { kind: "idle" },
  );
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  const refetchOrderDetailSilent = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      const d = await fetchCustomerOrder(getToken, orderId);
      setDetail(d);
    } catch {
      /* keep existing detail on transient errors */
    }
  }, [getToken, isSignedIn, orderId]);

  const trackingEnabled = Boolean(isSignedIn && detail && !error);
  // `driver.location` no longer reaches the customer namespace (compliance gate — see
  // CUSTOMER_NAMESPACE_DENYLIST in @the-drop/types + docs/live-driver-map-relocation.md). The hook
  // is still useful here for `liveMessage`, `onRunCompleted` (silent refetch), and `orderEta`
  // (live customer-safe ETA window). `driversById` is intentionally not destructured.
  const { liveMessage, socketConnected, orderEta } = useDriverLocationSocket({
    namespace: trackingEnabled && orderId ? wsOrdersNamespace(orderId) : null,
    getToken,
    enabled: trackingEnabled,
    orderStatus: detail?.order.status ?? null,
    onRunCompleted: () => {
      void refetchOrderDetailSilent();
    },
    apiBaseUrl: getCustomerApiBaseUrl() || undefined,
  });

  // Live updates win over the server-seeded `etaWindow`; the seed handles the gap between page
  // load and the first WS push (which can be up to 5 min away on the recompute tick). Both shapes
  // already obey the customer-safe contract — coordinates-free, stops-ahead-free. Normalize the
  // optional `runningLate` field from `OrderEtaPayload` into a definite boolean so the card's
  // three-state render doesn't have to triage `undefined` separately.
  const displayedEta: EtaCardWindow | null = orderEta
    ? {
        etaStart: orderEta.etaStart,
        etaEnd: orderEta.etaEnd,
        computedAt: orderEta.computedAt,
        runningLate: orderEta.runningLate === true,
      }
    : (detail?.etaWindow ?? null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const d = await fetchCustomerOrder(getToken, orderId);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error && e.message === "order_not_found"
              ? "Order not found."
              : "Could not load this order.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, orderId, getToken]);

  /** Auto-reset the "Copied" label after a short window. */
  useEffect(() => {
    if (copiedAt == null) return;
    const id = window.setTimeout(() => setCopiedAt(null), COPY_LABEL_RESET_MS);
    return () => window.clearTimeout(id);
  }, [copiedAt]);

  const shopSlug = detail?.dispensary.publicSlug ?? dispensarySlug;
  const status = detail?.order.status ?? null;
  const heading = status ? trackingHeading(status) : { h1: "Order", lede: "" };
  const copiedRecently = copiedAt != null;

  async function onCopyOrderId() {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopiedAt(Date.now());
    } catch {
      /* clipboard rejection (denied permission, http context) — silently no-op;
       *  the button still shows the short id below for manual copy. */
    }
  }

  function onDownloadReceipt(salesDeliveryStopId: string) {
    setReceiptState({ kind: "loading" });
    void (async () => {
      const r = await openSalesReceiptPdf(getToken, salesDeliveryStopId);
      if (r.ok) {
        setReceiptState({ kind: "idle" });
      } else {
        setReceiptState({ kind: "error", message: r.message });
      }
    })();
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
      <header className="space-y-3 border-b border-[var(--card-border)] pb-6">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">
            Order #{shortOrderId(orderId)}
          </p>
          <button
            type="button"
            onClick={onCopyOrderId}
            aria-label="Copy full order ID"
            className="text-[10px] font-medium uppercase tracking-[.1em] text-[var(--text-muted)] transition-colors hover:text-[var(--green)]"
          >
            {copiedRecently ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
              {heading.h1}
            </h1>
            {heading.lede ? (
              <p className="mt-1 text-sm text-[var(--text-secondary)]">{heading.lede}</p>
            ) : null}
          </div>
          {/* Q7 follow-up: surface receipt near the H1 when completed (in addition to bottom CTA). */}
          {detail?.order.status === "completed" && detail.salesDeliveryStopId ? (
            <button
              type="button"
              disabled={receiptState.kind === "loading"}
              onClick={() => onDownloadReceipt(detail.salesDeliveryStopId!)}
              className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {receiptState.kind === "loading" ? "Opening receipt…" : "Download receipt →"}
            </button>
          ) : null}
        </div>
      </header>

      <SignedOut>
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">Sign in to see updates for your order.</p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-3 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </SignedOut>

      <SignedIn>
        {loading ? (
          <p className="text-sm text-[var(--text-muted)]" aria-busy="true">
            Loading order…
          </p>
        ) : null}
        {error ? (
          <div
            role="alert"
            className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
          >
            {error}
          </div>
        ) : null}

        {detail && !error ? (
          <>
            {/* Timeline + map side-by-side on md+; stacked on mobile.
             *  Both are content-driven, but the map is wider visually so
             *  it gets the larger column (3:5 split). Cancelled / failed
             *  orders render the timeline as a banner; map is hidden, so
             *  the timeline drops back to a single column on those
             *  terminal states. */}
            {detail.order.status !== "cancelled" && detail.order.status !== "failed" ? (
              <div className="grid gap-4 md:grid-cols-5">
                <div className="md:col-span-2">
                  <OrderTrackingTimeline
                    status={detail.order.status}
                    dispensaryName={detail.dispensary.displayName}
                  />
                </div>
                <div className="md:col-span-3">
                  <LiveStatusCard
                    socketConnected={socketConnected}
                    liveMessage={liveMessage}
                    etaWindow={displayedEta}
                  />
                </div>
              </div>
            ) : (
              <OrderTrackingTimeline
                status={detail.order.status}
                dispensaryName={detail.dispensary.displayName}
              />
            )}

            {detail.order.status === "completed" && detail.salesDeliveryStopId ? (
              <ReceiptCta
                state={receiptState}
                onClick={() => onDownloadReceipt(detail.salesDeliveryStopId!)}
              />
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <DeliverySummary detail={detail} />
              <ItemsSummary detail={detail} />
            </div>
          </>
        ) : null}
      </SignedIn>

      <div className="border-t border-[var(--card-border)] pt-6">
        <Link
          to="/$dispensarySlug/menu"
          params={{ dispensarySlug: shopSlug }}
          className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
        >
          ← Back to menu
        </Link>
      </div>
    </div>
  );
}

/* ─── Subcomponents ─────────────────────────────────────────────── */

function OrderTrackingTimeline({
  status,
  dispensaryName,
}: {
  status: OrderStatus;
  dispensaryName: string;
}) {
  if (status === "cancelled") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">Order cancelled</p>
        <p className="mt-1">{dispensaryName} cancelled this order before delivery.</p>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div
        role="alert"
        className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
      >
        <p className="font-semibold">Delivery couldn't be completed</p>
        <p className="mt-1">Reach out to support and reference this order.</p>
      </div>
    );
  }

  return (
    /**
     * `aria-live="polite"` so socket-pushed status changes (e.g. dispatched →
     * in_progress) get announced to SR users — without this, the Live step
     * shifts visually but reader users get nothing (redline 06 flag 4).
     */
    <section
      aria-live="polite"
      aria-atomic="false"
      className="space-y-3 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd"
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
          Order timeline
        </p>
        <p className="text-xs text-[var(--text-muted)]">{dispensaryName}</p>
      </div>
      <ol className="space-y-0">
        {TRACK_LABELS.map((label, i) => {
          const state = timelineState(status, i);
          const isLast = i === TRACK_LABELS.length - 1;
          return (
            <TimelineRow
              key={label}
              label={label}
              state={state}
              showConnector={!isLast}
            />
          );
        })}
      </ol>
    </section>
  );
}

function TimelineRow({
  label,
  state,
  showConnector,
}: {
  label: string;
  state: "complete" | "live" | "pending";
  showConnector: boolean;
}) {
  const dotCls =
    state === "complete"
      ? "bg-[var(--green)] border-[var(--green)]"
      : state === "live"
      ? "bg-[var(--green)] border-[var(--green)] motion-safe:animate-pulse motion-safe:ring-4 motion-safe:ring-[var(--green)]/25"
      : "bg-white border-[var(--card-border)]";
  const labelCls =
    state === "pending"
      ? "text-[var(--text-muted)]"
      : "text-[var(--text-primary)]";
  const labelWeight = state === "live" ? "font-semibold" : "font-medium";
  const connectorCls =
    state === "complete" ? "bg-[var(--green)]" : "bg-[var(--card-border)]";
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center pt-1">
        <span
          aria-hidden
          className={`block h-3 w-3 shrink-0 rounded-full border-2 ${dotCls}`}
        />
        {showConnector ? (
          <span aria-hidden className={`mt-1 h-8 w-px ${connectorCls}`} />
        ) : null}
      </div>
      <div className={`flex-1 pb-3 ${showConnector ? "" : "pb-0"}`}>
        <p className={`text-sm ${labelWeight} ${labelCls}`}>
          {label}
          {/* sr-only state announcement so screen-reader users still hear
           *  Complete / Live / Pending — sighted users get the dot fill +
           *  pulse cues. Drops the visible right-side label that read as
           *  redundant once the dot pattern was clear. */}
          <span className="sr-only">
            {state === "complete"
              ? " — complete"
              : state === "live"
              ? " — in progress"
              : " — pending"}
          </span>
        </p>
      </div>
    </li>
  );
}

/**
 * Order-status card. Three visual states driven by `etaWindow`:
 *
 *   - **pre-run** (no `etaWindow`): "Preparing your order" copy from `docs/driver-eta.md`.
 *     This is the dominant state of an order's lifetime (~1-2 hours from placed → driver accepts
 *     per the doc); deserves real design attention not just placeholder copy.
 *   - **window** (`etaWindow`, not running late): "Arriving 4:25 – 4:40 PM" — the customer-safe
 *     promise. No countdown timer (data updates on the minute, not the second; a ticking clock
 *     implies precision we don't have).
 *   - **running-late** (`etaWindow.runningLate === true`): "Running a little late" with a softened
 *     window update. Copy obeys the customer-disclosure rule from
 *     `feedback_customer_driver_disclosure.md` — generic acknowledgment ("a few other stops along
 *     the way") is OK; never how many, when, or where.
 *
 * `socketConnected` + `liveMessage` are preserved across all three states for the "Reconnecting…"
 * affordance and run-status text.
 */
function LiveStatusCard({
  socketConnected,
  liveMessage,
  etaWindow,
}: {
  socketConnected: boolean;
  liveMessage: string | null;
  etaWindow: EtaCardWindow | null;
}) {
  return (
    <section className="space-y-3 overflow-hidden rounded-2xl border border-[var(--card-border)] bg-white shadow-cardMd">
      <div className="relative">
        <EtaCardBody etaWindow={etaWindow} />

        {/* Reconnecting pill — only visible when socket has dropped. Top-left placement preserved
         *  from the prior map-overlay design even though there's no map anymore, so the UX cue
         *  stays consistent across the redesign. */}
        {!socketConnected ? (
          <span
            role="status"
            className="absolute left-3 top-3 z-10 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 shadow-cardMd"
          >
            Reconnecting…
          </span>
        ) : null}
      </div>
      {/* liveMessage caption — contextual to the card, keeps the timeline visually clean.
       *  Two states:
       *   - live: a server-emitted run-status event arrived this session (DRIVER_ETA_UPDATE,
       *     RUN_ARRIVED_AT_DISPENSARY, RUN_COMPLETED — see the hook for the copy).
       *   - empty: generic "we'll update you" message. Deliberately not surfacing
       *     `lastDriverLocation.recordedAt` here — "driver last seen 5 min ago" is
       *     position-adjacent timing data, same compliance shape as coordinates. Tracked under
       *     "Adjacent exposure — below the line, awaiting legal" in
       *     docs/live-driver-map-relocation.md.
       */}
      <p className="px-4 pb-3 text-xs text-[var(--text-muted)]">
        {liveMessage ?? "We'll update this when your driver is on the road."}
      </p>
    </section>
  );
}

function DeliverySummary({ detail }: { detail: CustomerOrderDetail }) {
  return (
    <section className="space-y-2 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
        Delivery
      </p>
      <p className="text-sm text-[var(--text-primary)]">
        {detail.order.deliveryAddressLine1}
        {detail.order.deliveryAddressLine2 ? `, ${detail.order.deliveryAddressLine2}` : ""}
      </p>
      <p className="text-sm text-[var(--text-muted)]">
        {detail.order.deliveryCity}, {detail.order.deliveryState} {detail.order.deliveryZip}
      </p>
      {detail.order.deliveryPaymentMethod === "cash_at_delivery" ? (
        <p className="mt-2 inline-flex items-center rounded-full bg-[var(--surface-sage)] px-3 py-1 text-xs font-medium text-[var(--green-dark)]">
          Paying cash at delivery
        </p>
      ) : null}
    </section>
  );
}

function ItemsSummary({ detail }: { detail: CustomerOrderDetail }) {
  return (
    <section className="space-y-3 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
        Items
      </p>
      <ul className="space-y-2">
        {detail.lineItems.map((l) => (
          <li
            key={l.id}
            className="flex items-baseline justify-between gap-3 text-sm text-[var(--text-primary)]"
          >
            <span className="min-w-0 truncate">
              {l.productName}{" "}
              <span className="text-[var(--text-muted)]">
                × {l.quantity} {l.quantityUnit}
              </span>
            </span>
            <span className="shrink-0 font-medium">{formatCentsAsUsd(l.lineTotalCents)}</span>
          </li>
        ))}
      </ul>
      <p className="flex items-baseline justify-between border-t border-[var(--card-border)] pt-3 text-sm">
        <span className="font-medium text-[var(--text-secondary)]">Total</span>
        <span className="text-base font-bold text-[var(--text-primary)]">
          {formatCentsAsUsd(detail.order.totalCents)}
        </span>
      </p>
    </section>
  );
}

function ReceiptCta({
  state,
  onClick,
}: {
  state: { kind: "idle" | "loading" } | { kind: "error"; message: string };
  onClick: () => void;
}) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={state.kind === "loading"}
        onClick={onClick}
        className="inline-flex items-center rounded-lg border border-[var(--card-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:border-[var(--green)] hover:text-[var(--green)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {state.kind === "loading" ? "Opening receipt…" : "Download sales receipt (PDF)"}
      </button>
      {state.kind === "error" ? (
        <p
          role="status"
          className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900"
        >
          Couldn't open receipt — {state.message}
        </p>
      ) : null}
    </div>
  );
}
